/**
 * New Relic Browser Agent utilities for custom instrumentation and user tracking
 * Security RX features enabled: Session Replay, CSP monitoring, error tracking
 */

/**
 * Set custom user attributes for New Relic tracking
 * @param {Object} user - User object with id, email, organization info
 */
export const setNewRelicUser = (user) => {
  if (typeof window !== 'undefined' && window.newrelic) {
    try {
      window.newrelic.setCustomAttribute('userId', user.id);
      window.newrelic.setCustomAttribute('userEmail', user.email);
      if (user.organizationId) {
        window.newrelic.setCustomAttribute(
          'organizationId',
          user.organizationId
        );
      }
      if (user.organizationName) {
        window.newrelic.setCustomAttribute(
          'organizationName',
          user.organizationName
        );
      }
      if (user.role) {
        window.newrelic.setCustomAttribute('userRole', user.role);
      }
    } catch (error) {
      console.error('Failed to set New Relic user attributes:', error);
    }
  }
};

/**
 * Clear user attributes on logout
 */
export const clearNewRelicUser = () => {
  if (typeof window !== 'undefined' && window.newrelic) {
    try {
      window.newrelic.setCustomAttribute('userId', null);
      window.newrelic.setCustomAttribute('userEmail', null);
      window.newrelic.setCustomAttribute('organizationId', null);
      window.newrelic.setCustomAttribute('organizationName', null);
      window.newrelic.setCustomAttribute('userRole', null);
    } catch (error) {
      console.error('Failed to clear New Relic user attributes:', error);
    }
  }
};

/**
 * Track custom page action/interaction
 * @param {string} actionName - Name of the action
 * @param {Object} attributes - Additional attributes to track
 */
export const trackPageAction = (actionName, attributes = {}) => {
  if (typeof window !== 'undefined' && window.newrelic) {
    try {
      window.newrelic.addPageAction(actionName, attributes);
    } catch (error) {
      console.error('Failed to track page action:', error);
    }
  }
};

/**
 * Manually record an error with custom context
 * @param {Error} error - Error object
 * @param {Object} customAttributes - Additional context
 */
export const noticeError = (error, customAttributes = {}) => {
  if (typeof window !== 'undefined' && window.newrelic) {
    try {
      window.newrelic.noticeError(error, customAttributes);
    } catch (e) {
      console.error('Failed to send error to New Relic:', e);
    }
  }
};

/**
 * Set custom attributes for current page view
 * @param {Object} attributes - Key-value pairs of custom attributes
 */
export const setPageViewAttributes = (attributes = {}) => {
  if (typeof window !== 'undefined' && window.newrelic) {
    try {
      Object.keys(attributes).forEach((key) => {
        window.newrelic.setCustomAttribute(key, attributes[key]);
      });
    } catch (error) {
      console.error('Failed to set page view attributes:', error);
    }
  }
};

/**
 * Create custom timing for performance monitoring
 * @param {string} name - Name of the timing metric
 */
export const addRelease = (version) => {
  if (typeof window !== 'undefined' && window.newrelic) {
    try {
      window.newrelic.addRelease('changesync-web', version);
    } catch (error) {
      console.error('Failed to add release version:', error);
    }
  }
};

/**
 * Track SPA route changes
 * @param {string} route - New route path
 */
export const setCurrentRouteName = (route) => {
  if (typeof window !== 'undefined' && window.newrelic) {
    try {
      window.newrelic.setCurrentRouteName(route);
    } catch (error) {
      console.error('Failed to set route name:', error);
    }
  }
};

/**
 * Create interaction for custom timing
 * @param {string} name - Interaction name
 * @param {Function} callback - Function to execute within interaction
 */
export const interaction = (name, callback) => {
  if (typeof window !== 'undefined' && window.newrelic) {
    try {
      const interaction = window.newrelic.interaction();
      interaction.setName(name);
      const result = callback();
      interaction.save();
      return result;
    } catch (error) {
      console.error('Failed to create interaction:', error);
      return callback();
    }
  }
  return callback();
};
